import { useRef, useState } from "react";
import { ChevronLeft, X } from "lucide-react";
import { Button, Screen, Text } from "@ferrivia/ui";
import { useQuestionnaireCopy } from "@ferrivia/questionnaire-ui";
import { useTranslation } from "@ferrivia/i18n";
import {
  INSTRUMENT_DEFINITIONS,
  isReachable,
  unansweredInSection,
  type AnswerMap,
  type AnswerValue,
  type InstrumentName,
  type ItemMap,
  type QuestionDefinition,
} from "@ferrivia/api-contract";
import { useKeyboardScrollOffset } from "../hooks/useKeyboardScrollOffset";
import { AutosaveChip } from "./AutosaveChip";
import type { SaveState } from "./useQuestionnaire";
import { ConditionalReveal } from "./questions/ConditionalReveal";
import { QuestionBlock } from "./questions/QuestionBlock";
import s from "./SectionScreen.module.css";

/** A section's questions as a tree rather than a flat list: a question whose
    `showIf` targets another question in the same section becomes that
    question's child instead of a sibling, so it can render nested under it
    and collapse/reveal with `ConditionalReveal` instead of popping in and
    out of a flat list. A retired question is never asked, so it is left out
    of the tree entirely; a question gated on one (the legacy cycle gate on
    `menstruation_regularity`) becomes a root. A root with a `showIf` still
    collapses with `ConditionalReveal`, so a stored legacy answer keeps
    hiding what it gates. */
function buildQuestionTree(questionKeys: readonly string[], questionsByKey: Map<string, QuestionDefinition>) {
  const keySet = new Set(questionKeys.filter((key) => !questionsByKey.get(key)?.retired));
  const childrenByParent = new Map<string, QuestionDefinition[]>();
  const roots: QuestionDefinition[] = [];
  for (const key of questionKeys) {
    const question = questionsByKey.get(key);
    if (!question || question.retired) continue;
    const parentKey = question.showIf?.key;
    if (parentKey && keySet.has(parentKey)) {
      const siblings = childrenByParent.get(parentKey) ?? [];
      siblings.push(question);
      childrenByParent.set(parentKey, siblings);
    } else {
      roots.push(question);
    }
  }
  return { roots, childrenByParent };
}

export interface SectionScreenProps {
  instrument: InstrumentName;
  sectionId: string;
  isLastSection: boolean;
  answers: AnswerMap;
  items: ItemMap;
  itemIndexes: ReadonlyMap<string, readonly number[]>;
  invalidKeys: ReadonlySet<string>;
  /** Questions still showing an untouched onboarding prefill. */
  prefilledKeys?: ReadonlySet<string>;
  /** Mark each reachable question still unanswered, and say how many at the
      top -- set when the client comes back to a section they left gaps in. */
  showMissing: boolean;
  save: SaveState;
  onAnswer(questionKey: string, value: AnswerValue): void;
  /** `itemIndex: null` opens the item editor on a new item. */
  onOpenItemEditor(questionKey: string, itemIndex: number | null): void;
  onRetrySave(): void;
  onBack(): void;
  onNext(): void;
  onClose(): void;
}

/** One section's questions, all rendered on one scrollable page in
    definition order. A follow-up question nests under the question that
    gates it and stays mounted at all times; `ConditionalReveal` animates it
    open or collapsed as its own reachability changes, rather than the page
    popping it in and out. */
export function SectionScreen({
  instrument,
  sectionId,
  isLastSection,
  answers,
  items,
  itemIndexes,
  invalidKeys,
  prefilledKeys = new Set(),
  showMissing,
  save,
  onAnswer,
  onOpenItemEditor,
  onRetrySave,
  onBack,
  onNext,
  onClose,
}: SectionScreenProps) {
  const { t } = useTranslation("client");
  const copy = useQuestionnaireCopy();
  const scrollRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const [screenElement, setScreenElement] = useState<HTMLDivElement | null>(null);
  // A typed answer sits low on the page, and the screen is fixed to the
  // layout viewport, so the keyboard would otherwise open straight over the
  // field being typed into with nothing scrollable to lift it clear.
  const keyboardHeight = useKeyboardScrollOffset(contentRef);

  const definition = INSTRUMENT_DEFINITIONS[instrument];
  const section = definition.sections.find((s) => s.id === sectionId);
  const questionsByKey = new Map(definition.questions.map((q) => [q.key, q]));
  const { roots, childrenByParent } = buildQuestionTree(section?.questionKeys ?? [], questionsByKey);
  const missingKeys = new Set(showMissing ? unansweredInSection(instrument, sectionId, answers, items) : []);

  function scrollToFirstMissing() {
    const first = scrollRef.current?.querySelector<HTMLElement>("[data-missing='true']");
    first?.scrollIntoView({ behavior: "smooth", block: "start" });
  }

  function renderQuestion(question: QuestionDefinition) {
    const questionBlock = (
      <QuestionBlock
        key={question.key}
        question={question}
        value={answers.get(question.key)}
        onChange={(value) => onAnswer(question.key, value)}
        errorText={invalidKeys.has(question.key) ? t("questionnaire.invalidAnswer", "Tarkista vastaus.") : undefined}
        prefilledHint={
          prefilledKeys.has(question.key)
            ? t(
                "questionnaire.prefilledHint",
                "Esitäytetty aiemman vastauksesi perusteella. Tarkista, että tiedot pitävät yhä paikkansa.",
              )
            : undefined
        }
        items={items.get(question.key) ?? []}
        itemIndexes={itemIndexes.get(question.key) ?? []}
        itemInvalidKeys={invalidKeys}
        onOpenEditor={(itemIndex) => onOpenItemEditor(question.key, itemIndex)}
        sheetContainer={screenElement}
      />
    );
    const isMissing = missingKeys.has(question.key);
    // Always wrapped, missing or not: toggling the wrapper on the first
    // keystroke changed the element tree and remounted the input, dropping
    // focus mid-number.
    const block = (
      <div key={question.key} className={isMissing ? s.missing : undefined} data-missing={isMissing || undefined}>
        {isMissing ? <span className={s.missingTag}>{t("questionnaire.missingAnswer", "Vastaamatta")}</span> : null}
        {questionBlock}
      </div>
    );
    const children = childrenByParent.get(question.key);
    if (!children) return block;
    return (
      <div key={question.key}>
        {block}
        {children.map((child) => revealed(child))}
      </div>
    );
  }

  function revealed(question: QuestionDefinition) {
    return (
      <ConditionalReveal
        key={question.key}
        show={isReachable(instrument, question, answers)}
        label={copy.questionFor(question.key)?.label ?? question.key}
        scrollContainer={scrollRef}
      >
        {renderQuestion(question)}
      </ConditionalReveal>
    );
  }

  return (
    <Screen ref={setScreenElement} className={s.root}>
      <div className={s.header}>
        <button type="button" className={s.back} onClick={onBack}>
          <ChevronLeft aria-hidden size={18} />
          {t("questionnaire.back", "Takaisin")}
        </button>
        <button type="button" className={s.close} onClick={onClose} aria-label={t("questionnaire.close", "Sulje")}>
          <X aria-hidden size={20} />
        </button>
      </div>

      <div className={s.scroll} ref={scrollRef}>
        <div ref={contentRef}>
          <Text as="p" size="xs" weight="medium" className={s.eyebrow}>
            {copy.sectionFor(sectionId)?.title ?? sectionId}
          </Text>
          {copy.sectionFor(sectionId)?.intro ? (
            <Text as="p" size="md" className={s.intro}>
              {copy.sectionFor(sectionId)?.intro}
            </Text>
          ) : null}

          {missingKeys.size > 0 ? (
            <div className={s.missingSummary}>
              <Text as="p" size="sm" className={s.missingSummaryText}>
                {t("questionnaire.missingSummary", {
                  count: missingKeys.size,
                  defaultValue: "{{count}} kysymystä on vielä vastaamatta. Ne on merkitty alle.",
                })}
              </Text>
              <button type="button" className={s.missingSummaryLink} onClick={scrollToFirstMissing}>
                {t("questionnaire.missingShow", "Näytä")}
              </button>
            </div>
          ) : null}

          {roots.map((question) => (question.showIf ? revealed(question) : renderQuestion(question)))}

          {/* In the scroll flow, not pinned to the viewport bottom: a fixed
              Jatka sits under the thumb while the question above it is still
              unread, which made skipping content the easiest thing to do. */}
          <Button className={s.continue} onClick={onNext}>
            {isLastSection ? t("questionnaire.finishSection", "Valmis") : t("questionnaire.continue", "Jatka")}
          </Button>
        </div>
        {/* Only while the keyboard is up: the room the focused field is
            scrolled into, which the screen has none of otherwise. */}
        {keyboardHeight > 0 ? <div aria-hidden="true" style={{ height: keyboardHeight }} /> : null}
      </div>

      <div className={s.footer}>
        <AutosaveChip save={save} onRetry={onRetrySave} />
      </div>
    </Screen>
  );
}
